import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { collectionLabel } from "@/lib/collections";
import {
  Eye, Wand2, Monitor, Tablet, Smartphone, Sparkles, PlayCircle, LayoutTemplate, Users,
} from "lucide-react";

const RESPONSIVE = [
  { key: "desktop", icon: Monitor, label: "Desktop" },
  { key: "tablet", icon: Tablet, label: "Tablet" },
  { key: "mobile", icon: Smartphone, label: "Mobile" },
];

// Category-agnostic product-type labels (kept local to avoid a page↔component cycle).
const TYPE_LABELS = {
  website_template: "Website Design", business_plan: "Business Plan", ai_agent: "AI Agent",
  course: "Course", toolkit: "Toolkit", consulting: "Consulting", funding_package: "Funding Package",
  digital_product: "Digital Product",
};

// DB-first Marketplace card. `template` is a normalized record derived from the
// database (Template Factory is the single source of truth). No hardcoded IDs.
export default function MarketplaceProductCard({ template, index = 0 }) {
  const navigate = useNavigate();
  const t = template;

  const openPreview = () => navigate(t.previewTo);
  const openDemo = () => navigate(t.demoTo);

  // Solution-first hierarchy: lead with the OUTCOME the buyer wants, then the
  // problem solved, then the mission — falling back to the description.
  const typeLabel = t.showcase ? "Concept Design" : (TYPE_LABELS[t.productType] || "Website Design");
  const leadText = t.hasSolution
    ? ((t.outcomes && t.outcomes[0]) || t.problemSolved || t.mission || t.description)
    : t.description;
  const idealRaw = (t.idealCustomer || "").trim().replace(/^(this is )?built for\s+/i, "").replace(/^for\s+/i, "");
  const shortIdeal = idealRaw.length > 48 ? idealRaw.slice(0, 48).trimEnd() + "…" : idealRaw;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col bg-navy-800 border border-white/10 hover:border-gold/50 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-black/40"
      data-testid={`template-card-${t.slug}`}
    >
      {/* Preview */}
      <div className="relative h-52 overflow-hidden cursor-pointer" onClick={openPreview}>
        {t.previewImage ? (
          <img src={t.previewImage} alt={`${t.name} — website design preview`} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full grid place-items-center bg-navy-700 text-gray-500 text-xs uppercase tracking-wide">{collectionLabel(t.category)}</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/10 to-transparent pointer-events-none" />

        {/* badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2 pointer-events-none">
          {t.showcase ? (
            <span data-testid={`badge-showcase-${t.slug}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-white/15 text-white px-2.5 py-1 font-bold backdrop-blur-sm"><LayoutTemplate size={11} /> Concept Demo</span>
          ) : (
            <>
              {(t.badges && t.badges.length ? t.badges : (t.featured ? ["Featured"] : [])).slice(0, 3).map((b) => (
                <span key={b} data-testid={`badge-${t.slug}-${b.toLowerCase().replace(/\s+/g, "-")}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-gold text-navy px-2.5 py-1 font-bold"><Sparkles size={11} /> {b}</span>
              ))}
              {t.isNew && <span data-testid={`badge-new-${t.slug}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-emerald-500 text-navy px-2.5 py-1 font-bold">New</span>}
            </>
          )}
        </div>

        {/* hover hint (visual only — never intercepts taps, so image always opens Preview) */}
        <div className="absolute inset-0 flex items-center justify-center bg-navy/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <span className="flex items-center gap-2 bg-white text-navy px-5 py-2.5 text-sm font-semibold">
            <Eye size={15} /> Preview
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 flex flex-col flex-1">
        {/* Product type + responsive support */}
        <div className="flex items-center justify-between mb-2">
          <span data-testid={`product-type-${t.slug}`} className="text-[11px] text-gold uppercase tracking-[0.18em] font-semibold">{typeLabel}</span>
          <div className="flex items-center gap-1.5" data-testid={`responsive-badges-${t.slug}`}>
            {RESPONSIVE.map((r) => t.responsive?.[r.key] && (
              <span key={r.key} title={r.label} className="text-gray-500" data-testid={`responsive-${r.key}-${t.slug}`}><r.icon size={13} /></span>
            ))}
          </div>
        </div>

        {/* Product name */}
        <h3 onClick={openPreview} data-testid={`preview-title-${t.slug}`} className="font-heading text-xl text-white mb-2 group-hover:text-gold transition-colors cursor-pointer">{t.name}</h3>

        {/* Outcome-led lead line (falls back to the description) */}
        <p onClick={openPreview} data-testid={`preview-desc-${t.slug}`} className="text-gray-300 text-sm leading-relaxed line-clamp-2 mb-3 cursor-pointer">{leadText}</p>

        {/* Ideal customer + category meta */}
        <div className="flex flex-wrap items-center gap-2 mb-5 mt-auto">
          {idealRaw && (
            <span data-testid={`card-ideal-${t.slug}`} className="inline-flex items-center gap-1.5 text-[11px] text-gray-200 bg-white/5 border border-white/10 px-2.5 py-1">
              <Users size={11} className="text-gold shrink-0" /> <span className="line-clamp-1">For {shortIdeal}</span>
            </span>
          )}
          <span onClick={openPreview} data-testid={`preview-category-${t.slug}`} className="text-[11px] text-gray-500 uppercase tracking-wide cursor-pointer">{collectionLabel(t.category)}</span>
        </div>

        {/* pricing — service oriented */}
        <div className="flex items-end justify-between mb-5 pt-4 border-t border-white/10">
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Custom build</div>
            <div className="font-heading text-2xl text-gold">Starting at $997</div>
          </div>
          <div className="text-right text-[11px] text-gray-500 leading-snug">Customized<br/>& launched for you</div>
        </div>

        {/* actions */}
        <div className={`grid ${t.hasDemo ? "grid-cols-2" : "grid-cols-1"} gap-3 mb-3`}>
          <button onClick={openPreview} data-testid={`preview-${t.slug}`}
            className="flex items-center justify-center gap-2 border border-white/25 text-white py-3 text-sm font-medium hover:bg-white/5 hover:border-gold/50 transition-colors">
            <Eye size={15} /> Preview
          </button>
          {t.hasDemo && (
            <button onClick={openDemo} data-testid={`demo-btn-${t.slug}`}
              className="flex items-center justify-center gap-2 border border-white/25 text-white py-3 text-sm font-medium hover:bg-white/5 hover:border-gold/50 transition-colors">
              <PlayCircle size={15} /> Live Demo
            </button>
          )}
        </div>

        <p className="text-center text-[12px] text-gray-400 mb-2" data-testid={`want-like-${t.slug}`}>Want one like this?</p>
        <button onClick={() => navigate(`/start?template=${t.slug}`)} data-testid={`customize-${t.slug}`}
          className="w-full flex items-center justify-center gap-2 bg-gold text-navy py-3 text-sm font-semibold hover:bg-gold-light transition-colors">
          <Wand2 size={15} /> Build This for My Business
        </button>
        <button onClick={() => navigate("/start")} data-testid={`custom-build-${t.slug}`}
          className="mt-2.5 w-full text-center text-[12px] text-gray-400 hover:text-gold transition-colors">
          Or we'll build one to fit your needs →
        </button>
      </div>
    </motion.div>
  );
}
